package link.erpango.quip.ui.ocr

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Surface
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.launch
import link.erpango.quip.domain.ocr.OcrError
import link.erpango.quip.domain.ocr.OcrPhotoOutcome
import link.erpango.quip.domain.ocr.OcrPhotoText
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipPrimaryButton
import link.erpango.quip.ui.components.QuipSecondaryButton
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.close
import quip.composeapp.generated.resources.note_import_camera_action
import quip.composeapp.generated.resources.note_import_camera_frame_guide
import quip.composeapp.generated.resources.note_import_camera_no_text
import quip.composeapp.generated.resources.note_import_camera_permission
import quip.composeapp.generated.resources.note_import_camera_retry
import quip.composeapp.generated.resources.note_import_camera_use_anyway
import quip.composeapp.generated.resources.note_import_ocr_loading

/** UI state of the capture screen. */
private sealed interface CapturePhase {
    data object Camera : CapturePhase
    data object Recognizing : CapturePhase
    data object NoText : CapturePhase
    data class Error(val error: OcrError) : CapturePhase
}

/**
 * Full-screen still-photo capture for the on-device OCR flow.
 *
 * It only takes the photo and runs [PlatformTextRecognition] on it; nothing is persisted. On a
 * successful recognition the text is delivered through [onTextRecognized] and the caller decides
 * how to merge it. When no text is found the user can retake the photo or continue anyway.
 */
@Composable
fun CapturePhotoScreen(
    onTextRecognized: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    var phase by remember { mutableStateOf<CapturePhase>(CapturePhase.Camera) }
    val scope = rememberCoroutineScope()

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Surface(modifier = Modifier.fillMaxSize(), color = Color.Black) {
            Box(modifier = Modifier.fillMaxSize()) {
                val trigger = PlatformPhotoCapture.rememberCamera(
                    modifier = Modifier.fillMaxSize(),
                    onPhotoCaptured = { bytes ->
                        scope.launch {
                            phase = CapturePhase.Recognizing
                            val result = PlatformTextRecognition.recognize(bytes)
                            phase = when (val outcome = OcrPhotoText.outcomeOf(result)) {
                                is OcrPhotoOutcome.Recognized -> {
                                    onTextRecognized(outcome.text)
                                    CapturePhase.Camera
                                }
                                OcrPhotoOutcome.Empty -> CapturePhase.NoText
                                is OcrPhotoOutcome.Failed -> CapturePhase.Error(outcome.error)
                            }
                        }
                    },
                    onError = { phase = CapturePhase.Error(it) },
                )

                if (phase is CapturePhase.Camera) {
                    DocumentFrameOverlay()
                }

                // Close button, always available.
                Row(
                    modifier = Modifier.fillMaxWidth().padding(QuipDimens.padding8),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    IconButton(onClick = onDismiss) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = stringResource(Res.string.close),
                            tint = Color.White,
                        )
                    }
                    Text(
                        text = stringResource(Res.string.note_import_camera_action),
                        style = MaterialTheme.typography.h6,
                        fontWeight = FontWeight.Bold,
                        color = Color.White,
                    )
                }

                val bottomCenter = Modifier.align(Alignment.BottomCenter)
                when (val current = phase) {
                    CapturePhase.Camera -> CameraControls(
                        modifier = bottomCenter,
                        onCapture = trigger::capture,
                        onCancel = onDismiss,
                    )
                    CapturePhase.Recognizing -> RecognizingControls()
                    CapturePhase.NoText -> NoTextControls(
                        modifier = bottomCenter,
                        onUseAnyway = onTextRecognized,
                        onRetry = { phase = CapturePhase.Camera },
                        onCancel = onDismiss,
                    )
                    is CapturePhase.Error -> ErrorControls(
                        modifier = bottomCenter,
                        error = current.error,
                        onRetry = { phase = CapturePhase.Camera },
                        onCancel = onDismiss,
                    )
                }
            }
        }
    }
}

/** A4-proportioned frame guiding the user to keep the document inside the shot. */
@Composable
private fun DocumentFrameOverlay() {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Box(modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.35f)))
        Column(
            modifier = Modifier.fillMaxWidth().padding(horizontal = QuipDimens.padding24),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = stringResource(Res.string.note_import_camera_frame_guide),
                style = MaterialTheme.typography.body2,
                color = Color.White,
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(QuipDimens.padding16))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.86f)
                    .aspectRatio(1f / 1.414f)
                    .border(
                        width = QuipDimens.padding2,
                        color = Color.White,
                        shape = RoundedCornerShape(QuipDimens.cornerRadius12),
                    )
            )
        }
    }
}

@Composable
private fun CameraControls(
    modifier: Modifier,
    onCapture: () -> Unit,
    onCancel: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(QuipDimens.padding24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(QuipDimens.padding16),
    ) {
        Box(
            modifier = Modifier
                .size(QuipDimens.iconSize48 + QuipDimens.iconSize28)
                .clip(CircleShape)
                .background(Color.White)
                .clickable(onClick = onCapture),
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .size(QuipDimens.iconSize48)
                    .clip(CircleShape)
                    .border(
                        width = QuipDimens.padding4,
                        color = Color.Black.copy(alpha = 0.35f),
                        shape = CircleShape,
                    ),
            )
        }
        QuipSecondaryButton(
            text = stringResource(Res.string.close),
            onClick = onCancel,
        )
    }
}

@Composable
private fun RecognizingControls() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(QuipDimens.padding16),
            modifier = Modifier.background(Color.Black.copy(alpha = 0.6f)).padding(QuipDimens.padding24),
        ) {
            CircularProgressIndicator(color = QuipColors.colorPrimary)
            Text(
                text = stringResource(Res.string.note_import_ocr_loading),
                style = MaterialTheme.typography.body1,
                color = Color.White,
            )
        }
    }
}

@Composable
private fun NoTextControls(
    modifier: Modifier,
    onUseAnyway: (String) -> Unit,
    onRetry: () -> Unit,
    onCancel: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(QuipDimens.padding24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(QuipDimens.padding12),
    ) {
        Text(
            text = stringResource(Res.string.note_import_camera_no_text),
            style = MaterialTheme.typography.body1,
            color = Color.White,
            textAlign = TextAlign.Center,
        )
        QuipPrimaryButton(
            text = stringResource(Res.string.note_import_camera_use_anyway),
            onClick = { onUseAnyway("") },
        )
        QuipSecondaryButton(
            text = stringResource(Res.string.note_import_camera_retry),
            onClick = onRetry,
        )
        QuipSecondaryButton(
            text = stringResource(Res.string.close),
            onClick = onCancel,
        )
    }
}

@Composable
private fun ErrorControls(
    modifier: Modifier,
    error: OcrError,
    onRetry: () -> Unit,
    onCancel: () -> Unit,
) {
    val message = if (error is OcrError.PermissionDenied) {
        stringResource(Res.string.note_import_camera_permission)
    } else {
        ocrErrorMessage(error)
    }
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(QuipDimens.padding24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(QuipDimens.padding12),
    ) {
        Text(
            text = message,
            style = MaterialTheme.typography.body1,
            color = QuipColors.colorWarning,
            textAlign = TextAlign.Center,
        )
        QuipSecondaryButton(
            text = stringResource(Res.string.note_import_camera_retry),
            onClick = onRetry,
        )
        QuipSecondaryButton(
            text = stringResource(Res.string.close),
            onClick = onCancel,
        )
    }
}
